import { TARGET_NETWORKS, wagmiConfig } from 'configurations/wagmi';
import { useCallback } from 'react';
import { authApi } from 'services/auth';
import { BaseError, type Address } from 'viem';
import { useAccount, useSignMessage, useSwitchChain } from 'wagmi';
import type { SignMessageData } from 'wagmi/query';

const DEFAULT_MESSAGE = 'Sign to verify ownership';

export const getUtmFromQuery = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return new URLSearchParams(window.location.search).get('utm') ?? undefined;
};

export const isUserRejectedRequestError = (error: unknown) => {
  return (
    error instanceof BaseError &&
    error.shortMessage?.toLowerCase().includes('user rejected')
  );
};

const isChainNotConfiguredError = (error: unknown) => {
  const message =
    error instanceof BaseError
      ? error.shortMessage
      : error instanceof Error
      ? error.message
      : undefined;

  if (!message) return false;

  return message.toLowerCase().includes('chain is not configured');
};

export const useWalletAuthentication = () => {
  const { address, connector, chainId } = useAccount();
  const { signMessageAsync } = useSignMessage({ config: wagmiConfig });
  const { switchChainAsync } = useSwitchChain({ config: wagmiConfig });

  const ensureTargetChain = useCallback(async () => {
    if (!connector) return;

    const activeChainId = chainId ?? (await connector.getChainId?.());

    if (!activeChainId || activeChainId === TARGET_NETWORKS.id) {
      return;
    }

    try {
      const canSwitchWithConnector =
        typeof connector.switchChain === 'function';
      const canSwitchWithHook = typeof switchChainAsync === 'function';

      if (canSwitchWithHook) {
        await switchChainAsync?.({ chainId: TARGET_NETWORKS.id });
        return;
      }

      if (canSwitchWithConnector) {
        await connector.switchChain?.({ chainId: TARGET_NETWORKS.id });
        return;
      }

      throw new Error(
        'Chain is not configured. Please switch to the supported network in your wallet.'
      );
    } catch (error) {
      if (isUserRejectedRequestError(error)) {
        throw error;
      }

      if (isChainNotConfiguredError(error)) {
        throw new Error(
          'Chain is not configured. Please switch to the supported network in your wallet.'
        );
      }

      throw error;
    }
  }, [chainId, connector, switchChainAsync]);

  return useCallback(
    async (message: string = DEFAULT_MESSAGE) => {
      if (!address) {
        throw new Error('Wallet is not connected');
      }

      let signature: SignMessageData;
      let existingUtm = localStorage.getItem('utm') || undefined;
      const referralCode = localStorage.getItem('referralCode') || undefined;
      
      if (!existingUtm) {
        const utm = getUtmFromQuery();
        if (utm) existingUtm = utm;
      }

      try {
        await ensureTargetChain();
        signature = await signMessageAsync({
          account: address as Address,
          message,
        });
      } catch (error) {
        if (isUserRejectedRequestError(error)) {
          throw error;
        }

        if (isChainNotConfiguredError(error)) {
          await ensureTargetChain();
          signature = await signMessageAsync({
            account: address as Address,
            message,
          });
          await authApi.auth(address, signature, existingUtm || undefined, referralCode);
          return signature;
        }

        const connectorWithSignMessage = connector as unknown as {
          signMessage?: (args: {
            account: Address;
            message: string;
          }) => Promise<SignMessageData>;
        };

        if (connectorWithSignMessage?.signMessage) {
          await ensureTargetChain();
          signature = await connectorWithSignMessage.signMessage({
            account: address as Address,
            message,
          });
        } else {
          throw error;
        }
      }

      await authApi.auth(address, signature, existingUtm || undefined, referralCode);

      return signature;
    },
    [address, connector, ensureTargetChain, signMessageAsync]
  );
};
